/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {CompoApiService, CrmIssue, CrmIssueElastic, IssueType} from 'compob2b-libs';
import {NbDialogRef} from '@nebular/theme';
import {Router} from '@angular/router';
import {FormControl, ValidationErrors, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith, switchMap} from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import {MAT_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MAT_SELECT_SCROLL_STRATEGY_PROVIDER} from '@angular/material/select';

@Component({
    selector: 'app-issue-epic-link',
    templateUrl: './issue-epic-link.component.html',
    styleUrls: ['./issue-epic-link.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        MAT_AUTOCOMPLETE_SCROLL_STRATEGY_FACTORY_PROVIDER,
        MAT_SELECT_SCROLL_STRATEGY_PROVIDER,
    ],
    standalone: false
})
export class IssueEpicLinkComponent implements OnInit {
  @Input() issue: CrmIssue;
  @Output() updateEpicNameEvent = new EventEmitter<string>();
  @Output() updateParentEvent = new EventEmitter<number | null>();

  epicNameControl: FormControl;
  autocompleteControl: FormControl;
  showAutocompleteForm: boolean;
  selectedIssueId: number;
  selectedIssue: CrmIssueElastic;
  epicList$: Observable<CrmIssueElastic[]>;

  @ViewChild('epicNameElement') epicNameElement: ElementRef<HTMLInputElement>;
  @ViewChild('epicSearchElement') epicSearchElement: ElementRef<HTMLInputElement>;

  constructor(private readonly dialogRef: NbDialogRef<any>,
              private readonly apiService: CompoApiService,
              private readonly cdr: ChangeDetectorRef,
              private readonly router: Router) {
  }

  ngOnInit(): void {
    this.epicNameControl = new FormControl(this.epicName, [Validators.required, this.noWhitespaceValidator]);
    this.showAutocompleteForm = false;
    this.selectedIssueId = this.isParentAnEpic ? this.issue.parent.id : null;
  }

  public noWhitespaceValidator(control: FormControl): ValidationErrors {
    const val = (typeof control.value === 'object') ? control.value.epicName : control.value;
    const isWhitespace = (val || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : {whitespace: true};
  }

  get isEpic(): boolean {
    return this.issue.crmIssueType.id === IssueType.EPIC;
  }

  get isParentAnEpic(): boolean {
    return this.issue.parent !== null && this.issue.parent.crmIssueType.id === IssueType.EPIC;
  }

  get epicName(): string {
    return this.isEpic ? this.issue.epicName : this.epicNameFromParent;
  }

  get epicNameFromParent(): string {
    return this.isParentAnEpic ? this.issue.parent.epicName : '';
  }

  openEpicModal($event): void {
    this.dialogRef.close();
    this.router.navigate(
      ['cabinet', 'crm', 'project', 'show', this.issue.parent.crmProjectId],
      {queryParams: {issueId: this.issue.parent.id}}).then();
    $event.stopPropagation();
  }

  changeEpicName() {
    if (this.epicNameControl.valid) {
      const epicName = this.epicNameControl.value.trim();
      if (epicName !== this.issue.epicName) {
        this.epicNameElement.nativeElement.blur();
        this.updateEpicNameEvent.emit(epicName);
      }
    }
  }

  showAutocomplete() {
    this.showAutocompleteForm = true;
    this.autocompleteControl = new FormControl(this.epicName, [Validators.required, this.noWhitespaceValidator]);
    setTimeout(() => this.epicSearchElement.nativeElement.focus());
    this.epicList$ = this.autocompleteControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => this.searchEpic(term))
      );
  }

  private searchEpic(term: string): Observable<CrmIssueElastic[]> {

    const url = '/issue/elasticsearch/epics';

    const params = new HttpParams()
      .set('keyword', term.toLowerCase())
      .set('projectId', this.issue.crmProjectId);

    return this.apiService.getWithParams<CrmIssueElastic[]>(url, params).pipe(
      map(response => response.data)
    );
  }

  deleteEpic() {
    this.issue.parent = null;
    this.showAutocompleteForm = false;
    this.updateParentEvent.emit(null);
  }

  onChangeEpic(event: MatAutocompleteSelectedEvent) {
    const issue: CrmIssueElastic = event.option.value;

    this.selectedIssue = issue;
    this.selectedIssueId = issue.id;
    this.autocompleteControl.setValue(this.selectedIssue.epicName);
    this.epicSearchElement.nativeElement.selectionEnd = 0;
    this.showAutocompleteForm = false;
    this.cdr.detectChanges();
    this.updateParentEvent.emit(issue.id);
  }

  onFocusOut() {
    setTimeout(() => {
      this.showAutocompleteForm = false
    }, 150);
  }
}
